import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Card, CardContent } from "../components/ui/card";
import { Progress } from "../components/ui/progress";
import { RadioGroup, RadioGroupItem } from "../components/ui/radio-group";
import { Checkbox } from "../components/ui/checkbox";
import { api, formatApiErrorDetail } from "../lib/api";
import { toast } from "sonner";
import { ChevronLeft, ChevronRight, Check } from "lucide-react";
import { track } from "../lib/analytics";

const ACTIVITY_LEVELS = [
  { value: "sedentary", label: "Sedentary", desc: "Mostly sitting, no exercise" },
  { value: "low", label: "Low", desc: "Light walking, occasional movement" },
  { value: "moderate", label: "Moderate", desc: "Exercise 2-3 times a week" },
  { value: "high", label: "High", desc: "Exercise 4-5 times a week" },
  { value: "very_high", label: "Very High", desc: "Daily intense training" },
];

const LIFESTYLE_OPTIONS = [
  { value: "smoker", label: "Smoking" },
  { value: "alcohol", label: "Regular alcohol" },
  { value: "poor_diet", label: "Poor / irregular diet" },
  { value: "high_stress", label: "High stress levels" },
  { value: "irregular_sleep", label: "Irregular sleep" },
];

const CONDITION_GROUPS = [
  {
    label: "Metabolism & weight",
    items: [
      { value: "weight_loss", label: "Weight loss" },
      { value: "fat_loss", label: "Fat loss" },
      { value: "belly_fat", label: "Belly fat" },
      { value: "slow_metabolism", label: "Slow metabolism" },
      { value: "weight_regain", label: "Weight regain" },
      { value: "constant_hunger", label: "Constant hunger" },
      { value: "sugar_cravings", label: "Sugar cravings" },
      { value: "stress_eating", label: "Stress eating" },
      { value: "emotional_eating", label: "Emotional eating" },
      { value: "difficulty_losing_weight", label: "Difficulty losing weight" },
    ],
  },
  {
    label: "Blood sugar & heart",
    items: [
      { value: "prediabetes", label: "Prediabetes" },
      { value: "diabetes_t2", label: "Type 2 diabetes" },
      { value: "high_blood_pressure", label: "High blood pressure" },
      { value: "high_cholesterol", label: "High cholesterol" },
      { value: "blood_sugar_imbalance", label: "Blood sugar imbalance" },
      { value: "energy_crashes", label: "Energy crashes after meals" },
    ],
  },
  {
    label: "Hormonal health",
    items: [
      { value: "pcos", label: "PCOS" },
      { value: "pcod", label: "PCOD" },
      { value: "hormonal_imbalance", label: "Hormonal imbalance" },
      { value: "menopause", label: "Menopause / perimenopause" },
      { value: "mood_swings", label: "Mood swings" },
      { value: "hot_flashes", label: "Hot flashes" },
      { value: "irregular_periods", label: "Irregular periods" },
    ],
  },
  {
    label: "Energy & mental clarity",
    items: [
      { value: "low_energy", label: "Low energy / fatigue" },
      { value: "brain_fog", label: "Brain fog" },
      { value: "poor_focus", label: "Poor focus" },
      { value: "low_motivation", label: "Low motivation" },
      { value: "burnout", label: "Burnout" },
      { value: "poor_sleep", label: "Poor sleep" },
      { value: "stress_anxiety", label: "Stress / anxiety" },
    ],
  },
  {
    label: "Digestion & inflammation",
    items: [
      { value: "bloating", label: "Bloating" },
      { value: "digestive_issues", label: "Digestive issues" },
      { value: "constipation", label: "Constipation" },
      { value: "acid_reflux", label: "Acid reflux" },
      { value: "gut_health_issues", label: "Gut health issues" },
      { value: "fatty_liver", label: "Fatty liver" },
      { value: "inflammation", label: "Inflammation" },
      { value: "water_retention", label: "Water retention" },
    ],
  },
  {
    label: "Skin & wellness",
    items: [
      { value: "skin_problems", label: "Skin problems" },
      { value: "acne", label: "Acne" },
      { value: "healthier_habits", label: "Want healthier habits" },
      { value: "general_wellness_support", label: "General wellness support" },
    ],
  },
];

// Special "Other" option lives outside the groups so we can render it
// distinctly with an expanding free-text field.
const OTHER_CONDITION_VALUE = "other_not_listed";

const GOALS = [
  "Lose weight",
  "Burn fat",
  "Boost energy",
  "Improve digestion",
  "Balance hormones",
  "Sleep better",
  "Reduce cravings",
  "Feel calmer",
  "Improve focus",
  "Support healthy blood sugar",
  "Improve confidence",
  "Feel lighter",
  "Build healthier habits",
  "Improve metabolism",
];

const BEHAVIOR = [
  { value: "analytical", label: "Analytical", desc: "Show me data, mechanisms, evidence." },
  { value: "emotional", label: "Empathetic", desc: "Support me with warmth, reassurance." },
  { value: "action_driven", label: "Action-driven", desc: "Just give me the clear next step." },
];

const TOTAL_STEPS = 5;

export default function QuestionnairePage() {
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);

  // Fire assessment_start once per mount
  useEffect(() => {
    track.assessmentStart();
  }, []);

  // Track each completed step
  useEffect(() => {
    if (step > 1) track.assessmentStep(step - 1);
  }, [step]);

  const [age, setAge] = useState("");
  const [gender, setGender] = useState("female");
  const [heightCm, setHeightCm] = useState("");
  const [weightKg, setWeightKg] = useState("");
  const [activityLevel, setActivityLevel] = useState("moderate");
  const [lifestyle, setLifestyle] = useState([]);
  const [conditions, setConditions] = useState([]);
  const [conditionsOther, setConditionsOther] = useState("");
  const [goals, setGoals] = useState([]);
  const [behaviorType, setBehaviorType] = useState("emotional");
  const [notes, setNotes] = useState("");

  const toggle = (arr, setArr, val) => {
    setArr(arr.includes(val) ? arr.filter((v) => v !== val) : [...arr, val]);
  };

  const canProceed = () => {
    if (step === 1) return Number(age) > 0 && !!gender;
    if (step === 2) return Number(heightCm) > 0 && Number(weightKg) > 0;
    if (step === 3) return !!activityLevel;
    return true;
  };

  const submit = async () => {
    setLoading(true);
    try {
      const { data } = await api.post("/assessments", {
        age: Number(age),
        gender,
        height_cm: Number(heightCm),
        weight_kg: Number(weightKg),
        activity_level: activityLevel,
        lifestyle,
        conditions,
        conditions_other: conditions.includes(OTHER_CONDITION_VALUE)
          ? (conditionsOther || "").trim() || null
          : null,
        goals,
        behavior_type: behaviorType,
        notes,
      });
      track.assessmentCompleted(data);
      // Phase D-8: distinguish guest vs registered for GA4 conversion funnel.
      try {
        const isGuest = !!localStorage.getItem("fg_guest_token") && !localStorage.getItem("fg_access_token");
        if (isGuest) {
          // Lazy import to avoid touching consent-gated facade until needed.
          import("../lib/tracking").then(({ trackEvent }) => {
            trackEvent("guest_completed", {
              assessment_id: data?.id,
              location: "questionnaire",
            });
          });
        }
      } catch { /* swallow */ }
      toast.success("Great! Generating your personalized report…");
      navigate("/report");
    } catch (e) {
      const msg = formatApiErrorDetail(e.response?.data?.detail) || e.message;
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <main className="bg-leaf-pattern min-h-[calc(100vh-4rem)] py-10" data-testid="questionnaire-page">
      <div className="max-w-2xl mx-auto px-4 sm:px-6">
        <div className="text-center mb-6">
          <div className="text-xs uppercase tracking-[0.2em] text-emerald-700 font-semibold">
            Health Assessment
          </div>
          <h1 className="font-display text-3xl sm:text-4xl text-emerald-950 font-light tracking-tight mt-2">
            Let's build your picture.
          </h1>
          <p className="text-emerald-900/70 mt-2 text-sm">
            Answer honestly — the more accurate, the better your recommendations.
          </p>
        </div>

        <div className="flex items-center gap-3 mb-6">
          <Progress value={(step / TOTAL_STEPS) * 100} className="h-2 bg-emerald-100" />
          <div className="text-sm text-emerald-800 font-medium whitespace-nowrap" data-testid="step-indicator">
            {step} / {TOTAL_STEPS}
          </div>
        </div>

        <Card className="rounded-2xl border-emerald-100">
          <CardContent className="p-6 sm:p-8">
            {step === 1 && (
              <div className="step-enter space-y-5" data-testid="step-basics">
                <h2 className="font-display text-xl text-emerald-950">About you</h2>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="age" className="text-emerald-900">Age</Label>
                    <Input
                      id="age"
                      type="number"
                      value={age}
                      onChange={(e) => setAge(e.target.value)}
                      min={1}
                      max={120}
                      className="mt-1 rounded-xl border-emerald-200"
                      placeholder="e.g. 34"
                      data-testid="age-input"
                    />
                  </div>
                  <div>
                    <Label className="text-emerald-900">Gender</Label>
                    <RadioGroup value={gender} onValueChange={setGender} className="mt-2 grid grid-cols-3 gap-2" data-testid="gender-group">
                      {["female", "male", "other"].map((g) => (
                        <Label
                          key={g}
                          htmlFor={`g-${g}`}
                          className={`flex items-center justify-center rounded-xl border px-3 py-2 text-sm capitalize cursor-pointer ${
                            gender === g
                              ? "border-emerald-700 bg-emerald-50 text-emerald-900"
                              : "border-emerald-200 text-emerald-900/70 hover:bg-emerald-50"
                          }`}
                        >
                          <RadioGroupItem id={`g-${g}`} value={g} className="sr-only" data-testid={`gender-${g}`} />
                          {g}
                        </Label>
                      ))}
                    </RadioGroup>
                  </div>
                </div>
              </div>
            )}

            {step === 2 && (
              <div className="step-enter space-y-5" data-testid="step-measurements">
                <h2 className="font-display text-xl text-emerald-950">Body measurements</h2>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="height" className="text-emerald-900">Height (cm)</Label>
                    <Input
                      id="height"
                      type="number"
                      value={heightCm}
                      onChange={(e) => setHeightCm(e.target.value)}
                      min={50}
                      max={260}
                      className="mt-1 rounded-xl border-emerald-200"
                      placeholder="e.g. 168"
                      data-testid="height-input"
                    />
                  </div>
                  <div>
                    <Label htmlFor="weight" className="text-emerald-900">Weight (kg)</Label>
                    <Input
                      id="weight"
                      type="number"
                      value={weightKg}
                      onChange={(e) => setWeightKg(e.target.value)}
                      min={10}
                      max={500}
                      className="mt-1 rounded-xl border-emerald-200"
                      placeholder="e.g. 72"
                      data-testid="weight-input"
                    />
                  </div>
                </div>
              </div>
            )}

            {step === 3 && (
              <div className="step-enter space-y-5" data-testid="step-activity">
                <h2 className="font-display text-xl text-emerald-950">Activity & lifestyle</h2>
                <div>
                  <Label className="text-emerald-900">Activity level</Label>
                  <RadioGroup
                    value={activityLevel}
                    onValueChange={setActivityLevel}
                    className="mt-3 grid sm:grid-cols-2 gap-2"
                    data-testid="activity-group"
                  >
                    {ACTIVITY_LEVELS.map((a) => (
                      <Label
                        key={a.value}
                        htmlFor={`a-${a.value}`}
                        className={`rounded-xl border px-4 py-3 cursor-pointer ${
                          activityLevel === a.value
                            ? "border-emerald-700 bg-emerald-50"
                            : "border-emerald-200 hover:bg-emerald-50"
                        }`}
                      >
                        <RadioGroupItem id={`a-${a.value}`} value={a.value} className="sr-only" data-testid={`activity-${a.value}`} />
                        <div className="font-medium text-emerald-950">{a.label}</div>
                        <div className="text-xs text-emerald-900/60">{a.desc}</div>
                      </Label>
                    ))}
                  </RadioGroup>
                </div>
                <div>
                  <Label className="text-emerald-900">Lifestyle factors (select all that apply)</Label>
                  <div className="mt-3 flex flex-wrap gap-2">
                    {LIFESTYLE_OPTIONS.map((l) => {
                      const active = lifestyle.includes(l.value);
                      return (
                        <button
                          type="button"
                          key={l.value}
                          onClick={() => toggle(lifestyle, setLifestyle, l.value)}
                          className={`rounded-full px-4 py-2 text-sm border transition ${
                            active
                              ? "bg-emerald-700 text-white border-emerald-700"
                              : "bg-white text-emerald-900 border-emerald-200 hover:bg-emerald-50"
                          }`}
                          data-testid={`lifestyle-${l.value}`}
                        >
                          {l.label}
                        </button>
                      );
                    })}
                  </div>
                </div>
              </div>
            )}

            {step === 4 && (
              <div className="step-enter space-y-7" data-testid="step-conditions">
                <div>
                  <h2 className="font-display text-xl sm:text-2xl text-emerald-950">
                    What are you currently struggling with?
                  </h2>
                  <p className="text-sm text-emerald-900/70 mt-1.5 leading-relaxed">
                    Select anything that feels relevant to you — this helps us personalise your wellness recommendations.
                  </p>
                </div>

                <div className="space-y-5" data-testid="condition-groups">
                  {CONDITION_GROUPS.map((group) => (
                    <div key={group.label}>
                      <div className="text-[11px] uppercase tracking-[0.2em] text-emerald-700 font-semibold">
                        {group.label}
                      </div>
                      <div className="mt-2 flex flex-wrap gap-2">
                        {group.items.map((c) => {
                          const active = conditions.includes(c.value);
                          return (
                            <button
                              type="button"
                              key={c.value}
                              onClick={() => toggle(conditions, setConditions, c.value)}
                              aria-pressed={active}
                              className={`rounded-full px-3.5 py-2 text-sm border transition select-none ${
                                active
                                  ? "bg-emerald-700 text-white border-emerald-700 shadow-sm"
                                  : "bg-white text-emerald-900 border-emerald-200 hover:bg-emerald-50"
                              }`}
                              data-testid={`condition-${c.value}`}
                            >
                              {active ? "✓ " : ""}{c.label}
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  ))}

                  {/* Other / not listed — special expandable card */}
                  <div>
                    <div className="text-[11px] uppercase tracking-[0.2em] text-emerald-700 font-semibold">
                      Something else
                    </div>
                    <div className="mt-2">
                      <button
                        type="button"
                        onClick={() => {
                          const adding = !conditions.includes(OTHER_CONDITION_VALUE);
                          toggle(conditions, setConditions, OTHER_CONDITION_VALUE);
                          if (!adding) setConditionsOther("");
                        }}
                        aria-pressed={conditions.includes(OTHER_CONDITION_VALUE)}
                        className={`rounded-full px-3.5 py-2 text-sm border transition select-none ${
                          conditions.includes(OTHER_CONDITION_VALUE)
                            ? "bg-emerald-700 text-white border-emerald-700 shadow-sm"
                            : "bg-white text-emerald-900 border-emerald-200 hover:bg-emerald-50"
                        }`}
                        data-testid={`condition-${OTHER_CONDITION_VALUE}`}
                      >
                        {conditions.includes(OTHER_CONDITION_VALUE) ? "✓ " : ""}Other / not listed
                      </button>
                    </div>

                    {conditions.includes(OTHER_CONDITION_VALUE) ? (
                      <div className="mt-3 rounded-xl border border-emerald-200 bg-emerald-50/40 p-4" data-testid="condition-other-card">
                        <Label htmlFor="conditions-other" className="text-emerald-900 text-sm">
                          Tell us anything else you'd like support with
                        </Label>
                        <Textarea
                          id="conditions-other"
                          value={conditionsOther}
                          onChange={(e) => setConditionsOther(e.target.value.slice(0, 600))}
                          rows={3}
                          maxLength={600}
                          className="mt-1.5 rounded-xl border-emerald-200 resize-none"
                          placeholder="Tell us anything else you'd like support with — thyroid, autoimmune, medications, recovery, anything unique to you…"
                          data-testid="conditions-other-input"
                        />
                        <div className="flex items-start justify-between gap-3 mt-2">
                          {conditionsOther.trim().length >= 4 ? (
                            <p className="text-xs text-emerald-800 leading-relaxed flex-1" data-testid="conditions-other-helper">
                              <span className="font-semibold">Thanks for sharing.</span>{" "}
                              Some health concerns benefit from more personalised support — our wellness team can help guide you further.
                            </p>
                          ) : (
                            <p className="text-xs text-emerald-900/60 leading-relaxed flex-1">
                              Optional — we keep this private and use it only to personalise your recommendations.
                            </p>
                          )}
                          <span className="text-[11px] text-emerald-900/50 shrink-0 font-mono">
                            {conditionsOther.length}/600
                          </span>
                        </div>
                      </div>
                    ) : null}
                  </div>
                </div>

                <div>
                  <h2 className="font-display text-lg sm:text-xl text-emerald-950">
                    What would you most like to improve?
                  </h2>
                  <p className="text-sm text-emerald-900/70 mt-1">
                    Pick the goals that matter most right now.
                  </p>
                  <div className="mt-3 flex flex-wrap gap-2" data-testid="goal-list">
                    {GOALS.map((g) => {
                      const active = goals.includes(g);
                      return (
                        <button
                          type="button"
                          key={g}
                          onClick={() => toggle(goals, setGoals, g)}
                          aria-pressed={active}
                          className={`rounded-full px-4 py-2 text-sm border transition select-none ${
                            active
                              ? "bg-amber-400 text-emerald-950 border-amber-400 shadow-sm"
                              : "bg-white text-emerald-900 border-emerald-200 hover:bg-amber-50"
                          }`}
                          data-testid={`goal-${g.replace(/\s+/g, "-").toLowerCase()}`}
                        >
                          {active ? "✓ " : ""}{g}
                        </button>
                      );
                    })}
                  </div>
                </div>
              </div>
            )}

            {step === 5 && (
              <div className="step-enter space-y-5" data-testid="step-style">
                <h2 className="font-display text-xl text-emerald-950">How should we speak to you?</h2>
                <p className="text-sm text-emerald-900/70 -mt-2">
                  We'll adapt your report's tone so it actually feels like yours.
                </p>
                <RadioGroup value={behaviorType} onValueChange={setBehaviorType} className="grid gap-2" data-testid="behavior-group">
                  {BEHAVIOR.map((b) => (
                    <Label
                      key={b.value}
                      htmlFor={`b-${b.value}`}
                      className={`rounded-xl border px-4 py-3 cursor-pointer ${
                        behaviorType === b.value ? "border-emerald-700 bg-emerald-50" : "border-emerald-200 hover:bg-emerald-50"
                      }`}
                    >
                      <RadioGroupItem id={`b-${b.value}`} value={b.value} className="sr-only" data-testid={`behavior-${b.value}`} />
                      <div className="font-medium text-emerald-950">{b.label}</div>
                      <div className="text-xs text-emerald-900/60">{b.desc}</div>
                    </Label>
                  ))}
                </RadioGroup>
                <div>
                  <Label htmlFor="notes" className="text-emerald-900">Anything else to share? (optional)</Label>
                  <Textarea
                    id="notes"
                    value={notes}
                    onChange={(e) => setNotes(e.target.value)}
                    className="mt-1 rounded-xl border-emerald-200"
                    rows={3}
                    placeholder="Medications, allergies, preferences…"
                    data-testid="notes-input"
                  />
                </div>
              </div>
            )}

            <div className="mt-8 flex items-center justify-between">
              <Button
                type="button"
                variant="ghost"
                onClick={() => setStep((s) => Math.max(1, s - 1))}
                disabled={step === 1 || loading}
                className="rounded-full text-emerald-900 hover:bg-emerald-50"
                data-testid="step-back-btn"
              >
                <ChevronLeft className="w-4 h-4 mr-1" /> Back
              </Button>
              {step < TOTAL_STEPS ? (
                <Button
                  type="button"
                  onClick={() => setStep((s) => s + 1)}
                  disabled={!canProceed()}
                  className="rounded-full bg-emerald-700 hover:bg-emerald-800 text-white px-6"
                  data-testid="step-next-btn"
                >
                  Next <ChevronRight className="w-4 h-4 ml-1" />
                </Button>
              ) : (
                <Button
                  type="button"
                  onClick={submit}
                  disabled={loading}
                  className="rounded-full bg-emerald-700 hover:bg-emerald-800 text-white px-6"
                  data-testid="step-submit-btn"
                >
                  {loading ? "Analyzing…" : (<><Check className="w-4 h-4 mr-1" /> See my report</>)}
                </Button>
              )}
            </div>
          </CardContent>
        </Card>
      </div>
    </main>
  );
}
